
import React from 'react';
import { AlertCircle, ExternalLink } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface SearchDropdownAlertProps {
  type: 'no-api-key' | 'mock-data' | 'error' | 'cors-error';
  errorMessage?: string;
}

const SearchDropdownAlert: React.FC<SearchDropdownAlertProps> = ({ type, errorMessage }) => {
  if (type === 'no-api-key') {
    return (
      <Alert className="m-2 py-2 bg-blue-50 border-blue-100">
        <div className="flex gap-2 items-start">
          <AlertCircle className="h-4 w-4 mt-0.5 text-blue-600" />
          <AlertDescription className="text-sm text-blue-700">
            Google Maps API key is required for business search.
            <div className="mt-1 text-xs">
              <a 
                href="/settings" 
                className="text-blue-600 flex items-center gap-1 hover:underline"
              >
                Set up API key <ExternalLink size={12} />
              </a>
            </div>
          </AlertDescription>
        </div>
      </Alert>
    );
  }

  if (type === 'mock-data') {
    return (
      <Alert className="m-2 py-2 bg-yellow-50 border-yellow-100">
        <div className="flex gap-2 items-start">
          <AlertCircle className="h-4 w-4 mt-0.5 text-yellow-600" />
          <AlertDescription className="text-sm text-yellow-700">
            Using sample data. API access issue detected.
            <div className="mt-1 text-xs text-yellow-600">
              Your API key may need Places API enabled or CORS configuration.
            </div>
          </AlertDescription>
        </div>
      </Alert>
    );
  }
  
  if (type === 'cors-error') {
    return (
      <Alert className="m-2 py-2 bg-amber-50 border-amber-100">
        <div className="flex gap-2 items-start">
          <AlertCircle className="h-4 w-4 mt-0.5 text-amber-600" />
          <AlertDescription className="text-sm text-amber-700">
            CORS proxy access needed for development
            <div className="mt-1 text-xs">
              <a 
                href="https://cors-anywhere.herokuapp.com/corsdemo" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-600 flex items-center gap-1 hover:underline"
              >
                Click here to request temporary access <ExternalLink size={12} />
              </a>
            </div>
          </AlertDescription>
        </div>
      </Alert>
    );
  }

  if (type === 'error') {
    return (
      <div className="p-4 text-sm text-red-500 flex items-start gap-2">
        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div>
          <div>{errorMessage}</div>
          <div className="mt-1 text-xs text-gray-500">
            {!errorMessage?.includes('API key') ? (
              <span>Try a different search term or create a new business.</span>
            ) : (
              <div className="space-y-1">
                <span>Your API key may be invalid or doesn't have the Places API enabled.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SearchDropdownAlert;
